import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

const NATIVE_BILLING_BASE_URL = 'xpsr://billing';
const MOBILE_USER_AGENT_PATTERN = /Android|iPhone|iPad|iPod|Mobile/i;
const ANDROID_USER_AGENT_PATTERN = /Android/i;
const ANDROID_APP_PACKAGE = 'com.xpsr';

const buildNativeBillingReturnUrl = (
  isCancelPath: boolean,
  sessionId: string | null,
) => {
  const searchParams = new URLSearchParams();

  if (sessionId) {
    searchParams.set('session_id', sessionId);
  }

  const search = searchParams.toString();
  return `${NATIVE_BILLING_BASE_URL}/${isCancelPath ? 'cancel' : 'success'}${
    search ? `?${search}` : ''
  }`;
};

const buildAndroidIntentBillingReturnUrl = (
  isCancelPath: boolean,
  sessionId: string | null,
  webFallbackPath: string,
) => {
  const route = `billing/${isCancelPath ? 'cancel' : 'success'}`;
  const searchParams = new URLSearchParams();

  if (sessionId) {
    searchParams.set('session_id', sessionId);
  }

  const search = searchParams.toString();
  return `intent://${route}${search ? `?${search}` : ''}#Intent;scheme=xpsr;package=${ANDROID_APP_PACKAGE};S.browser_fallback_url=${encodeURIComponent(webFallbackPath)};end`;
};

const shouldOpenNativeBillingReturn = () =>
  typeof window !== 'undefined' &&
  MOBILE_USER_AGENT_PATTERN.test(window.navigator.userAgent);

const isAndroidBrowser = () =>
  typeof window !== 'undefined' &&
  ANDROID_USER_AGENT_PATTERN.test(window.navigator.userAgent);

const BillingRedirectPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const currentSearchParams = new URLSearchParams(location.search);
    const nextSearchParams = new URLSearchParams();
    const isCancelPath = location.pathname.endsWith('/cancel');
    const sessionId = currentSearchParams.get('session_id');

    nextSearchParams.set('billing', isCancelPath ? 'cancel' : 'success');

    if (sessionId) {
      nextSearchParams.set('session_id', sessionId);
    }

    const webFallbackPath = `/settings?${nextSearchParams.toString()}`;
    if (shouldOpenNativeBillingReturn()) {
      try {
        window.location.assign(
          isAndroidBrowser()
            ? buildAndroidIntentBillingReturnUrl(
                isCancelPath,
                sessionId,
                webFallbackPath,
              )
            : buildNativeBillingReturnUrl(isCancelPath, sessionId),
        );
      } catch {
        // Browser support for native app links varies; keep this bridge visible.
      }
    } else {
      navigate(webFallbackPath, {replace: true});
    }
  }, [location.pathname, location.search, navigate]);

  const currentSearchParams = new URLSearchParams(location.search);
  const isCancelPath = location.pathname.endsWith('/cancel');
  const sessionId = currentSearchParams.get('session_id');
  const nextSearchParams = new URLSearchParams();

  nextSearchParams.set('billing', isCancelPath ? 'cancel' : 'success');

  if (sessionId) {
    nextSearchParams.set('session_id', sessionId);
  }

  const webFallbackPath = `/settings?${nextSearchParams.toString()}`;
  const nativeBillingReturnUrl =
    typeof window !== 'undefined' && isAndroidBrowser()
      ? buildAndroidIntentBillingReturnUrl(
          isCancelPath,
          sessionId,
          webFallbackPath,
        )
      : buildNativeBillingReturnUrl(isCancelPath, sessionId);

  return (
    <div className="container">
      <h1>Returning to XPSR</h1>
      <p>If the mobile app did not open automatically, open it to finish billing.</p>
      <p>
        <a href={nativeBillingReturnUrl}>Open XPSR app</a>
      </p>
      <p>
        <a href={webFallbackPath}>Continue on web</a>
      </p>
    </div>
  );
};

export default BillingRedirectPage;
